<template>
  <wb-accordion class="otb-filter-accordion">
    <wb-accordion-item>
      <wb-accordion-toggle>
        <div
          class="otb-filter-accordion__toggle-label"
          :class="selectedValueClass"
        >
          <wb-icon
            v-if="showToggleIcon"
            :name="icon"
            class="otb-filter-accordion__toggle-label-icon"
          ></wb-icon
          >{{ label }}
        </div>
        <div>
          <wb-icon
            v-if="selectedState"
            name="bds/checkmark-circle/16"
            class="otb-filter-accordion__toggle-selected-icon"
          ></wb-icon>
          <wb-counter
            v-if="selectedCount"
            :value="selectedCount"
            class="otb-filter-accordion__toggle-counter"
            mode="primary"
          ></wb-counter>
        </div>
      </wb-accordion-toggle>
      <wb-accordion-section>
        <wb-accordion-mobile-header>{{
          $t('schedule.filter.back')
        }}</wb-accordion-mobile-header>
        <wb-accordion-content class="otb-filter-accordion__content">
          <div class="otb-filter-accordion__close-filters-btn">
            <button
              type="button"
              :aria-label="$t('schedule.filtersMenu.mobile.button.close')"
              class="wb-round-button wb-round-button--small"
              @click="closeAccordion"
            >
              <wb-icon
                class="otb-filter-accordion__close-filters-icon wb-round-button__icon"
                aria-hidden="true"
                name="close"
              ></wb-icon>
              {{ $t('schedule.filtersMenu.mobile.button.close') }}
            </button>
          </div>
          <slot>
            <div class="otb-filter-accordion__title">
              <wb-icon
                v-if="icon"
                class="otb-filter-accordion__title-icon"
                :name="icon"
              ></wb-icon>
              <span class="wb-type-copy-strong">{{ title || label }}</span>
            </div>
            <wb-card
              v-for="option in options"
              class="otb-filter-accordion__card"
              :class="
                (checkedClass(option.value), notificationClass(option.value))
              "
              :disabled="option.disabled"
              :key="`${name}-${option.value}`"
            >
              <div class="otb-filter-accordion__card-option">
                <label
                  class="wb-type-button-secondary otb-filter-accordion__option-label"
                  v-html="option.text"
                  :for="`${name}-${option.value}`"
                ></label>

                <div>
                  <wb-radio-control>
                    <label class="otb-filter-accordion__input">
                      <input
                        tabindex="-1"
                        type="radio"
                        :id="`${name}-${option.value}`"
                        :name="name"
                        :value="option.value"
                        :checked="isChecked(option.value)"
                        @change="inputChange"
                      />
                    </label>
                  </wb-radio-control>
                </div>
              </div>

              <div
                v-if="shouldShowAccordionNotification(option)"
                class="otb-filter-accordion__card-notification"
              >
                <wb-icon :name="notification.icon"></wb-icon>
                <p
                  class="otb-filter-accordion__card-notification__text wb-type-copy-secondary"
                >
                  {{ notification.text }}
                </p>
              </div>
            </wb-card>
          </slot>
        </wb-accordion-content>
      </wb-accordion-section>
    </wb-accordion-item>
  </wb-accordion>
</template>
<script lang="ts">
import { WEIGHT_CATEGORY } from '@dh-io-otb/otbc/src/models/constants';
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { TranslateResult } from 'vue-i18n';

export default defineComponent({
  name: 'otb-filter-accordion',
  props: {
    icon: String,
    notification: {},
    hideToggleIcon: Boolean,
    label: String || (String as TranslateResult),
    name: String,
    options: Array,
    selectedCount: Number,
    selectedState: Boolean,
    selectedValue: String,
    title: String || (String as TranslateResult),
  },
  setup(props, { emit }) {
    const selectedValue = ref('');
    const isChecked = (value: string): boolean => {
      return selectedValue.value === value;
    };

    const checkedClass = (value): string =>
      isChecked(value) ? 'otb-filter-accordion__toggle-label--checked' : '';

    const notificationClass = (value): string =>
      value === WEIGHT_CATEGORY.HEAVY_WEIGHT
        ? 'otb-filter-accordion__card-notification-container'
        : '';

    const shouldShowAccordionNotification = (option): any => {
      return (
        props.notification &&
        option?.value === props.notification?.value &&
        isChecked(option.value)
      );
    };

    const inputChange = (event: Event): void => {
      const value = (event.target as HTMLInputElement).value;
      selectedValue.value = value;
      emit('change', {
        name: props.name,
        value: selectedValue.value,
      });
    };

    const closeAccordion = (): void => {
      emit('close');
    };

    const selectedValueClass = computed(() => {
      return props.selectedState || props.selectedCount
        ? 'otb-filter-accordion__toggle-selected'
        : '';
    });

    const showToggleIcon = computed(() => props.icon && !props.hideToggleIcon);

    watch(
      () => props.selectedValue,
      (newValue) => {
        if (newValue) {
          selectedValue.value = newValue;
        }
      }
    );
    onMounted((): void => {
      if (props.selectedValue) {
        selectedValue.value = props.selectedValue;
      }
    });

    return {
      checkedClass,
      closeAccordion,
      isChecked,
      inputChange,
      notificationClass,
      shouldShowAccordionNotification,

      // COMPUTED
      selectedValueClass,
      showToggleIcon,
    };
  },
});
</script>
<style lang="scss" src="./filter-accordion.scss"></style>
